/**
 * Client-tier projections (F-2.14, A20, A62–A68). PURE.
 *
 * Three tiers, each built by WHITELISTING fields into an explicit type — never
 * by deleting fields from the full record — so a field added to the check later
 * is invisible to a lower tier until someone deliberately adds it here:
 *
 *   internal_detailed  everything: every funnel step, every rate with its
 *                      provenance, the risk components and weights, the audit.
 *   internal_simple    the answer and why, without the rate table.
 *   client             what a client may see (A68 is the rule): the label, the
 *                      projection, the accrual curve (A65, accepted tradeoff),
 *                      quantified remediation (A64), the validity statement
 *                      (A67) and the read-back of their own criteria (A63).
 *                      Never: rate values, pool or vendor names, risk score or
 *                      weights, the audit trail, anything internal.
 *
 * The projection runs in the Next handler whose response reaches the browser
 * (D14 AC (b)), so the browser never receives an un-projected record.
 */
import { describeResidents } from '../ooh/residents';
import type { ExclusiveCellsResult } from './exclusive-cells';
import type { CheckRecord, RemediationOption } from '../engine-store';
import { airportLabel } from '../ooh/airports';
import { describePlacements } from '../ooh/placements';
import { profileFieldLabel } from './profile-fields';
import type { FeasibilityRequest } from './request';
import { placeWords } from './visit-words';
import { plainEvidence } from '../analysis/plain-words';
import type {
    AccrualPoint,
    CellResult,
    FunnelResult,
    RunEvidence,
    IncidenceScenarioResult,
    PhasePlan,
    SharedAudience,
    Verdict,
    InvitationBasis,
    ReleaseOption,
} from './types';
import { daysWord } from './days-word';

export type ViewTier = 'internal_detailed' | 'internal_simple' | 'client';

export interface Validity {
    validUntil: string;
    expired: boolean;
    days: number;
}

export function validityOf(createdAt: string, validityDays: number, now: Date): Validity {
    const until = new Date(new Date(createdAt).getTime() + validityDays * 86_400_000);
    return { validUntil: until.toISOString(), expired: now > until, days: validityDays };
}

export const VERDICT_LABEL: Record<Verdict, string> = {
    feasible: 'Feasible',
    // Positive (UAT, 2026-10-15): it reaches the target, with a small cushion.
    marginal: 'Feasible, tight',
    // Never a dead end (2026-10-15): a check short of its target leads to the options.
    not_feasible: 'Options to increase feasibility',
    ceiling: 'Maximum achievable',
    cannot_estimate: 'Needs a custom estimate',
};

/**
 * The label for a check that has no verdict yet: still working, or stopped.
 * A failed check never reads "Calculating" (E2E run, 2026-10-04).
 */
export function pendingLabel(status: CheckRecord['status']): string {
    return status === 'failed' ? 'Could not finish' : 'Calculating';
}

/** What the client sees of their own request: the criteria, never the entry path or internal keys. */
export interface ClientReadBack {
    studyTypeLabel: string;
    target: number | null;
    incidenceRate: number;
    fieldDays: number;
    fieldStart: string | null;
    criteria: string[];
    /**
     * What screens people in before any survey question, when the study is
     * event-triggered (draft Amendment 13, A176): the event's own words, never
     * its rate. Null when only the screener qualifies people.
     */
    eventQualify?: string | null;
}

/**
 * The events a result screens people in by, from its "Event qualify: …" steps,
 * in the order they apply ("reached at the store, then location verified").
 */
export function eventQualifyOf(
    result: Pick<FunnelResult, 'steps'> | null | undefined,
): string | null {
    const events = [
        ...new Set(
            (result?.steps ?? [])
                .map((s) => /^Event qualify: (.+)$/.exec(s.label)?.[1])
                .filter((e): e is string => !!e),
        ),
    ];
    return events.length ? events.join(', then ') : null;
}

export interface ClientCheckView {
    tier: 'client';
    id: string;
    title: string | null;
    status: CheckRecord['status'];
    /** Still working: the job is running, or the fixes are still coming. Pollers stop when false. */
    waiting: boolean;
    label: string;
    verdict: Verdict | null;
    projected: number | null;
    targetReachedDay: number | null;
    accrual: AccrualPoint[];
    remediation: { label: string; description: string; projected: number; verdictLabel: string }[];
    validity: { validUntil: string; message: string } | null;
    readBack: ClientReadBack;
    /** Quota cells, whitelisted to what a client asked for (A84, A68). */
    cells: ClientCell[] | null;
    /** The answer at each other incidence rate asked for. */
    atIncidence: ClientIncidenceAnswer[] | null;
    sharedAudience: SharedAudience | null;
    /** Mutually exclusive cells, whitelisted: each cell's completes with nobody in two. */
    exclusiveCells: ClientExclusiveCells | null;
    recruitsNeeded: number | null;
    /**
     * A multi-phase study that cannot reach its target: the most who can finish
     * every phase and the recruits for them (UAT UI-022), what a client can plan on.
     */
    achievable: { finish: number; recruits: number } | null;
    /** Multi-phase studies: how many phases the recruits must finish. */
    phases: number | null;
    /** The cells are an out-of-home study's test and control groups. */
    cellsKind: 'test_control' | 'store' | 'airport' | 'market' | null;
    /**
     * What the estimate rests on (draft Amendment 14, A270, A280): how many past
     * studies like this, and how many of them were this client's own. Never a
     * study, a name or another client's figure. Null when the funnel answered.
     */
    basedOn: { studies: number; ownStudies: number } | null;
    createdAt: string;
}

export interface ClientExclusiveCells {
    cells: { label: string | null; target: number | null; projected: number; meets: boolean }[];
    total: number;
    fits: boolean;
}

export interface ClientIncidenceAnswer {
    label: string | null;
    incidenceRate: number;
    target: number | null;
    projected: number;
    verdictLabel: string;
    /** Multi-phase studies: the group's recruits into phase 1. */
    recruits: number | null;
}

export interface ClientCell {
    label: string;
    target: number | null;
    projected: number;
    verdictLabel: string;
    hardest: boolean;
}

/** The one rule for "still working" (complete with remediation null = the follow-up is running). */
export function isWaiting(check: Pick<CheckRecord, 'status' | 'remediation'>): boolean {
    return (
        check.status === 'queued' ||
        check.status === 'running' ||
        (check.status === 'complete' && check.remediation === null)
    );
}

export interface SimpleCheckView {
    tier: 'internal_simple';
    id: string;
    title: string | null;
    status: CheckRecord['status'];
    progress: number;
    progressLabel: string | null;
    error: string | null;
    verdict: Verdict | null;
    label: string;
    projected: number | null;
    range: FunnelResult['range'] | null;
    target: number | null;
    targetReachedDay: number | null;
    poolLabel: string | null;
    poolCount: number | null;
    eligibilityFilter: string | null;
    accrual: AccrualPoint[];
    risk: { score: number; band: string; drivers: string[] } | null;
    /** The engine's caveats, except provisional rates, which have their own field. */
    warnings: string[];
    /**
     * Provisional rates behind this result (Amendment 3, A78): how many, and
     * whether confirming them could change the answer. Null when none.
     * Results saved before the engine computed it read as could-change.
     */
    provisional: { count: number; couldChangeVerdict: boolean } | null;
    refusal: FunnelResult['refusal'];
    remediation: RemediationOption[] | null;
    validity: Validity | null;
    approval: {
        state: CheckRecord['approvalState'];
        by: string | null;
        at: string | null;
        note: string | null;
    };
    request: FeasibilityRequest;
    createdByEmail: string;
    createdAt: string;
    rateSetVersion: number | null;
    /** Quota cells (Amendment 4): each group's answer, and the one that holds the study back. */
    cells: CellResult[] | null;
    bindingCell: string | null;
    quotaMode: 'independent' | 'interlocked' | null;
    atIncidence: IncidenceScenarioResult[] | null;
    sharedAudience: SharedAudience | null;
    /** Mutually exclusive cells (exclusive-cells.ts). */
    exclusiveCells: ExclusiveCellsResult | null;
    /** Multi-phase studies: people to recruit into phase 1 so the target finishes. */
    recruitsNeeded: number | null;
    recruitsRange: { low: number; high: number } | null;
    /** Multi-phase studies: about how many are left at each phase. */
    phasePlan: PhasePlan | null;
    /** A multi-phase study short of its target: the plan for the number it can reach. */
    achievablePlan: PhasePlan | null;
    /** Who the estimate assumes is invited: capacity, or the planned sends (UAT backtest, 2026-10-15). */
    invitations: InvitationBasis | null;
    releaseOptions: ReleaseOption[] | null;
    /** The cells are an out-of-home study's test and control groups. */
    cellsKind: 'test_control' | 'store' | 'airport' | 'market' | null;
    /** What the two-tier run rested on (draft Amendment 14); staff see all of it (A280). */
    evidence: RunEvidence | null;
}

export interface DetailedCheckView extends Omit<SimpleCheckView, 'tier'> {
    tier: 'internal_detailed';
    result: FunnelResult | null;
}

export function toSimpleView(check: CheckRecord, validity: Validity | null): SimpleCheckView {
    const r = check.result;
    return {
        tier: 'internal_simple',
        id: check.id,
        title: check.title,
        status: check.status,
        progress: check.progress,
        progressLabel: check.progressLabel,
        error: check.error,
        verdict: check.verdict,
        label: check.verdict ? VERDICT_LABEL[check.verdict] : pendingLabel(check.status),
        projected: check.projected,
        range: r?.range ?? null,
        target: check.target,
        targetReachedDay: r?.targetReachedDay ?? null,
        poolLabel: r?.pool.label ?? null,
        poolCount: check.poolCount,
        eligibilityFilter: r?.pool.eligibilityFilter ?? null,
        accrual: r?.accrual ?? [],
        risk: r?.risk ? { score: r.risk.score, band: r.risk.band, drivers: r.risk.drivers } : null,
        warnings:
            r?.warnings.filter((w) => w.code !== 'provisional_rates').map((w) => w.message) ?? [],
        provisional: r?.warnings.some((w) => w.code === 'provisional_rates')
            ? {
                  count: uniqueProvisional(r.factors),
                  couldChangeVerdict: r.provisionalCouldChangeVerdict ?? true,
              }
            : null,
        refusal: r?.refusal ?? null,
        remediation: check.remediation,
        validity,
        approval: {
            state: check.approvalState,
            by: check.approvedByEmail,
            at: check.approvedAt,
            note: check.approvalNote,
        },
        request: check.request,
        createdByEmail: check.createdByEmail,
        createdAt: check.createdAt,
        rateSetVersion: check.rateSetVersion,
        cells: r?.cells ?? null,
        bindingCell: r?.bindingCell ?? null,
        quotaMode: r?.quotaMode ?? null,
        atIncidence: r?.atIncidence ?? null,
        sharedAudience: r?.sharedAudience ?? null,
        exclusiveCells: r?.exclusiveCells ?? null,
        recruitsNeeded: r?.recruitsNeeded ?? null,
        recruitsRange: r?.recruitsRange
            ? { low: r.recruitsRange.low, high: r.recruitsRange.high }
            : null,
        phasePlan: r?.phasePlan ?? null,
        achievablePlan: r?.achievablePlan ?? null,
        cellsKind: r?.cellsKind ?? null,
        evidence: plainEvidence(r?.evidence),
        invitations: r?.invitations ?? null,
        releaseOptions: r?.releaseOptions ?? null,
    };
}

export function toDetailedView(check: CheckRecord, validity: Validity | null): DetailedCheckView {
    return { ...toSimpleView(check, validity), tier: 'internal_detailed', result: check.result };
}

export function toClientView(
    check: CheckRecord,
    validity: Validity | null,
    studyTypeLabel: string,
    describeCriteria: (request: FeasibilityRequest) => string[],
): ClientCheckView {
    const r = check.result;
    const verdict = check.verdict;
    const label =
        verdict === 'ceiling'
            ? `Up to ${(check.projected ?? 0).toLocaleString('en-US')} completes`
            : verdict
              ? VERDICT_LABEL[verdict]
              : pendingLabel(check.status);
    return {
        tier: 'client',
        id: check.id,
        title: check.title,
        status: check.status,
        waiting: isWaiting(check),
        label,
        verdict,
        projected: check.projected,
        targetReachedDay: r?.targetReachedDay ?? null,
        accrual: r?.accrual ?? [],
        remediation: (check.remediation ?? []).map((o) => ({
            label: o.label,
            description: o.description,
            projected: o.projected,
            verdictLabel: VERDICT_LABEL[o.verdict],
        })),
        validity: validity
            ? {
                  validUntil: validity.validUntil,
                  message: validity.expired
                      ? 'This estimate is more than 90 days old. We recommend running it again.'
                      : `Valid for ${validity.days} days, until ${readableDay(validity.validUntil)}.`,
              }
            : null,
        readBack: {
            studyTypeLabel,
            target: check.request.target,
            incidenceRate: check.request.incidenceRate,
            fieldDays: check.request.fieldDays,
            fieldStart: check.request.fieldStart ?? null,
            criteria: describeCriteria(check.request),
            eventQualify: eventQualifyOf(r),
        },
        cells: r?.cells
            ? r.cells.map((c) => ({
                  label: c.label,
                  target: c.target,
                  projected: c.projected,
                  verdictLabel: c.naturalFallout ? 'Expected split' : VERDICT_LABEL[c.verdict],
                  hardest: c.key === r.bindingCell,
              }))
            : null,
        atIncidence: r?.atIncidence
            ? r.atIncidence.map((a) => ({
                  label: a.label,
                  incidenceRate: a.incidenceRate,
                  target: a.target,
                  projected: a.projected,
                  verdictLabel:
                      a.verdict === 'ceiling'
                          ? `Up to ${a.projected.toLocaleString('en-US')}`
                          : VERDICT_LABEL[a.verdict],
                  recruits: a.recruits ?? null,
              }))
            : null,
        sharedAudience: r?.sharedAudience ?? null,
        exclusiveCells: r?.exclusiveCells
            ? {
                  cells: [...r.exclusiveCells.cells]
                      .sort((a, b) => a.order - b.order)
                      .map((c) => ({
                          label: c.label,
                          target: c.target,
                          projected: c.projected,
                          meets: c.meets,
                      })),
                  total: r.exclusiveCells.total,
                  fits: r.exclusiveCells.fits,
              }
            : null,
        recruitsNeeded: r?.recruitsNeeded ?? null,
        achievable: r?.achievablePlan
            ? {
                  finish: r.achievablePlan.people[r.achievablePlan.people.length - 1],
                  recruits: r.achievablePlan.people[0],
              }
            : null,
        phases: r?.phasePlan?.phases ?? null,
        cellsKind: r?.cellsKind ?? null,
        basedOn: r?.evidence
            ? { studies: r.evidence.studies, ownStudies: r.evidence.clientStudies }
            : null,
        createdAt: check.createdAt,
    };
}

/** Plain-language read-back of criteria (A24, A63). Shared by the read-back card and the client view. */
export function describeRequestCriteria(request: FeasibilityRequest): string[] {
    const out: string[] = [];
    const digital: string[] = [];
    for (const c of request.criteria) {
        if (c.kind === 'demographic') {
            if (c.age) out.push(`Ages ${c.age.min}–${c.age.max}`);
            if (c.genders?.length)
                out.push(
                    c.genders
                        .map((g) =>
                            g === 'non_binary' ? 'Non-binary' : g[0].toUpperCase() + g.slice(1),
                        )
                        .join(' or '),
                );
            if (c.states?.length) out.push(`State: ${c.states.join(', ')}`);
            if (c.dmas?.length) out.push(`DMA: ${c.dmas.join(', ')}`);
            if (c.regions?.length) out.push(`Region: ${c.regions.join(', ')}`);
            if (c.zips?.codes.length) {
                const n = c.zips.codes.length;
                const list =
                    n > 5
                        ? `${c.zips.codes.slice(0, 5).join(', ')} and ${n - 5} more`
                        : c.zips.codes.join(', ');
                out.push(
                    c.zips.radiusMiles > 0
                        ? `Within ${c.zips.radiusMiles} miles of zip ${n === 1 ? 'code' : 'codes'} ${list}`
                        : `In zip ${n === 1 ? 'code' : 'codes'} ${list}`,
                );
            }
            if (c.panelists?.ids.length)
                out.push(
                    `${c.panelists.exclude ? 'Not' : 'Only'} the ${c.panelists.ids.length.toLocaleString('en-US')} panelists in ${c.panelists.name}`,
                );
            for (const [field, answers] of Object.entries(c.profile ?? {}))
                out.push(`${profileFieldLabel(field)}: ${answers.join(', ')}`);
        } else if (c.kind === 'ooh_exposure') {
            const when = c.window.lastDays
                ? `in the last ${daysWord(c.window.lastDays)}`
                : `between ${c.window.from} and ${c.window.to}`;
            if (c.residents) out.push(`People ${describeResidents(c.residents)}`);
            else if (c.airports?.length)
                out.push(
                    `Visited ${c.airports
                        .map(
                            (a) =>
                                `${airportLabel(a.code)}${a.target ? ` (${a.target.toLocaleString('en-US')} completes)` : ''}`,
                        )
                        .join(', ')} airport ${when}`,
                );
            else
                out.push(
                    c.control
                        ? `Test group: passed ${describePlacements(c.placements)} ${when}`
                        : `Passed ${describePlacements(c.placements)} ${when}`,
                );
            if (c.control)
                out.push(
                    `Control group${c.control.target ? ` (${c.control.target.toLocaleString('en-US')} completes)` : ''}: passed ${describePlacements(c.control.placements)} ${when}, and no test location`,
                );
        } else if (c.kind === 'venue') {
            // A store list with each location names its stores, not whole chains (UAT UI-008).
            const stores = c.listName
                ? `the stores in ${c.listName}`
                : c.chains.map((ch) => ch.name).join(' or ');
            out.push(
                c.radiusMiles
                    ? `Within ${c.radiusMiles} miles of ${stores}`
                    : `Visits ${stores}${c.dwellMinutes ? ` for at least ${c.dwellMinutes} minutes` : ''}`,
            );
            // Completes per store (UAT MT-005).
            const byStore = c.chains.filter((ch) => ch.target);
            if (byStore.length)
                out.push(
                    `Completes by store: ${byStore.map((ch) => `${ch.name} ${ch.target?.toLocaleString('en-US')}`).join(', ')}`,
                );
        } else if (c.kind === 'device_switch') {
            const what = c.switchType === 'carrier' ? 'carrier' : 'phone brand';
            const from = c.from?.length ? ` from ${c.from.join(' or ')}` : '';
            out.push(`Switched ${what}${from} to ${c.to.join(' or ')} ${windowWords(c.window)}`);
        } else if (c.kind === 'survey_participation') {
            const verb = c.mode === 'include' ? 'Took part in' : 'Did not take part in';
            out.push(`${verb} survey ${c.surveyIds.join(', ')} (${c.outcomes.join(', ')})`);
        } else if (c.kind === 'store_visit') {
            const where = placeWords(
                c.chains.map((ch) => ch.name),
                c.categories,
                c.listName ?? (c.listId ? 'saved' : null),
                c.chainMatch === 'all' ? 'and' : 'or',
            );
            out.push(
                `Visited ${c.chainMatch === 'all' ? 'every one of ' : ''}${where} ${windowWords(c.window)}, for at least ${c.dwellMinutes} minutes`,
            );
        } else if (c.kind === 'purchase') {
            const spend = c.minSpend
                ? `, spending at least $${c.minSpend.toLocaleString('en-US')}`
                : '';
            out.push(`Bought from ${c.stores.join(' or ')} ${windowWords(c.window)}${spend}`);
        } else {
            const when = windowWords(c.window);
            digital.push(
                c.kind === 'app_usage'
                    ? `used ${c.apps.join(' or ')} ${when}`
                    : `visited ${c.domains.join(' or ')} ${when}`,
            );
        }
    }
    // An app and a website together are "either" (UAT MT-003): one line.
    if (digital.length) {
        const line = digital.join(', or ');
        out.push(line[0].toUpperCase() + line.slice(1));
    }
    if (!out.length) out.push('Everyone on the panel');
    if (request.quotas) {
        const how = request.quotas.mode === 'interlocked' ? 'every combination' : 'each on its own';
        for (const d of request.quotas.dimensions) {
            const cells = d.cells
                .map((c) => (c.target != null ? `${c.label} ${c.target}` : c.label))
                .join(', ');
            out.push(`Quota (${how}): ${cells}`);
        }
    }
    if (request.phases) out.push(`${request.phases} phases`);
    if (request.daysBetweenPhases === 0) out.push('No wait between phases');
    else if (request.daysBetweenPhases)
        out.push(`${request.daysBetweenPhases} days between phases`);
    if (request.productsTested === 0) out.push('No product tested');
    else if (request.productsTested)
        out.push(
            `${request.productsTested} product${request.productsTested === 1 ? '' : 's'} tested each`,
        );
    return out;
}

function windowWords(w: { lastDays?: number; from?: string; to?: string }): string {
    return w.lastDays ? `in the last ${daysWord(w.lastDays)}` : `between ${w.from} and ${w.to}`;
}

function uniqueProvisional(factors: FunnelResult['factors']): number {
    return new Set(factors.filter((f) => f.isPlaceholder).map((f) => f.factorKey)).size;
}

/** "Jan 18, 2027": how a person reads a day (the ISO day is kept in `validUntil`). */
function readableDay(iso: string): string {
    const t = Date.parse(iso);
    return Number.isNaN(t)
        ? iso.slice(0, 10)
        : new Intl.DateTimeFormat('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
          }).format(new Date(t));
}
